import React from 'react';
import styled, { css } from 'styled-components';
import { ContentfulImageAsset } from '../../../interfaces/ContentfulImageAsset';
import { PrimaryButtonLink, SecondaryButtonLink } from '../../button';
import { BodyText, Heading } from '../../typography';
import { breakpoint, theme } from '../../../theme';
import { useBreakpointMap } from '../../../utils';

export type ContentfulContentCardProps = {
  title: string;
  description: string;
  image: ContentfulImageAsset;
  urlLink: string;
  openInNewTab: boolean;
  button?: string;
  hasFeaturedCard?: boolean;
  altText: string;
};
const ContentCardWrapper = styled.div<{ hasFeaturedCard?: boolean }>`
  min-width: 214px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.neutrals.solidGrays[6]};
  border-radius: ${theme.borderRadius.default};
  ${({ hasFeaturedCard }) =>
    hasFeaturedCard &&
    css`
      ${breakpoint('lg')`
        flex-direction: row;
      `}
    `}
`;

const Container = styled.div<{ hasFeaturedCard?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.spacings[5].rem};
  padding: ${theme.spacings[5].rem} ${theme.spacings[4.5].rem}
    ${theme.spacings[5].rem} ${theme.spacings[4.5].rem};
  ${({ hasFeaturedCard }) =>
    hasFeaturedCard &&
    css`
      ${breakpoint('lg')`
        padding: 0 ${theme.spacings[12].rem};
        width: 50%;
      `}
    `}
`;

const CardContainer = styled.a`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  ${breakpoint('sm')`
    flex-basis: calc(50% - 24px);
    `}
  ${breakpoint('lg')`
    flex-basis:100%;
    `}
`;

const Image = styled.img<{ hasFeaturedCard?: boolean }>`
  border-radius: ${theme.borderRadius.default} ${theme.borderRadius.default} 0 0;
  width: 100%;
  height: 200px;
  object-fit: cover;

  ${({ hasFeaturedCard }) =>
    hasFeaturedCard &&
    css`
      ${breakpoint('lg')`
        width: 50%;
        height: 320px;
      `}
    `}
`;

const Title = styled(Heading).attrs({ size: 4 })`
  ${breakpoint('sm')`
      ${theme.typography.headings[4]['md-xl']}
    `}
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2].rem};
`;

const Link = styled.a`
  width: auto !important;
`;

export const ContentfulContentCard: React.FC<ContentfulContentCardProps> = ({
  hasFeaturedCard,
  ...data
}) => {
  const size = useBreakpointMap({ lg: 'desktop' });
  const isDesktop = size === 'desktop';
  return (
    <CardContainer
      href={data.urlLink}
      target={data?.openInNewTab ? '_blank' : '_self'}
    >
      <ContentCardWrapper hasFeaturedCard={hasFeaturedCard}>
        <Image
          src={data?.image?.url}
          alt={data?.altText}
          hasFeaturedCard={hasFeaturedCard}
        ></Image>
        <Container hasFeaturedCard={hasFeaturedCard}>
          <Wrapper>
            <Title>{data.title}</Title>
            <BodyText size={'md'}>{data.description}</BodyText>
          </Wrapper>
          {data?.button &&
            (hasFeaturedCard ? (
              <PrimaryButtonLink fullWidth={isDesktop ? false : true}>
                <Link
                  target={data?.openInNewTab ? '_blank' : '_self'}
                  href={data.urlLink}
                >
                  {data?.button}
                </Link>
              </PrimaryButtonLink>
            ) : (
              <SecondaryButtonLink fullWidth>
                <Link
                  target={data?.openInNewTab ? '_blank' : '_self'}
                  href={data.urlLink}
                >
                  {data?.button}
                </Link>
              </SecondaryButtonLink>
            ))}
        </Container>
      </ContentCardWrapper>
    </CardContainer>
  );
};
